import React, {lazy, Suspense, useEffect, useState} from "react";
import {BrowserRouter as Router, Routes, Route, Navigate, Outlet, useLocation} from "react-router-dom";
import Layout from "./Layout";
import RouteErrorBoundary from "./components/RouteErrorBoundary";

import 'bootstrap/dist/css/bootstrap.min.css';

import {getProfile} from "./functions/Api";
import {useAppSettings} from "./functions/AppSettings";
import {CHATROOM_SERVICE_PRIVILEGES} from "./functions/ServicePrivileges";

const Home = lazy(() => import("./pages/Home"));
const Login = lazy(() => import("./pages/Login"));
const ProductManagement = lazy(() => import("./pages/services/ProductManagement"));
const TutorialsAndGuide = lazy(() => import("./pages/services/TutorialsAndGuide"));
const SingleProduct = lazy(() => import("./pages/services/SingleProduct"));
const NotFound = lazy(() => import("./pages/NotFound"));
const MfgProductManagement = lazy(() => import("./pages/services/MfgProductManagement"));
const CustomerDevelopment = lazy(() => import("./pages/services/cdp/CustomerDevelopment"));
const CompanyProfile = lazy(() => import("./pages/services/cdp/CompanyProfile"));
const AddEditCompany = lazy(() => import("./pages/services/cdp/AddEditCompany"));
const AddEditContacts = lazy(() => import("./pages/services/cdp/AddEditContacts"));
const ContactProfile = lazy(() => import("./pages/services/cdp/ContactProfile"));
const Dashboard = lazy(() => import("./pages/services/Dashboard"));
const DepartmentTasks = lazy(() => import("./pages/services/tasks/DepartmentTasks"));
const DepartmentAssignTask = lazy(() => import("./pages/services/tasks/DepartmentAssignTask"));
const DepartmentCreateTask = lazy(() => import("./pages/services/tasks/DepartmentCreateTask"));
const DepartmentTeams = lazy(() => import("./pages/services/tasks/DepartmentTeams"));
const CompanyContacts = lazy(() => import("./pages/services/cdp/CompanyContacts"));
const UserProfile = lazy(() => import("./pages/UserProfile"));
const AdminPage = lazy(() => import("./pages/AdminPage"));
const MyDepartment = lazy(() => import("./pages/MyDepartment"));
const AdminUserPage = lazy(() => import("./pages/AdminUserPage"));
const Chatrooms = lazy(() => import("./pages/services/Chatrooms"));
const FormSubmissions = lazy(() => import("./pages/services/FormSubmissions"));

const CHATROOM_PRIVILEGES = CHATROOM_SERVICE_PRIVILEGES;

const RouteLoading = () => (
    <div style={{padding: 20}}>Loading ...</div>
);

const SafeOutlet = () => {
    const location = useLocation();
    return (
        <RouteErrorBoundary resetKey={location.pathname}>
            <Outlet/>
        </RouteErrorBoundary>
    );
};

function App() {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const {lang, setLang, version} = useAppSettings();

    useEffect(() => {
        const initAuth = async () => {
            try {
                const me = await getProfile();
                setUser(me);
            } catch {
                setUser(null);
            } finally {
                setLoading(false);
            }
        };
        initAuth();
    }, []);

    if (loading) {
        return <div style={{padding: 20}}>Initializing session ...</div>;
    }

    // Convert privilege string → array
    const privilegeList = user?.servicePrivilege
        ? user.servicePrivilege.split(",").map((code) => code.trim().toUpperCase()).filter(Boolean)
        : [];

    // Map serviceCode → Component
    const serviceComponents = {
        PRODUCT_MANAGE: ProductManagement,
        PRODUCT_OBSERVE: ProductManagement,
        TUTORIALS_GUIDE: TutorialsAndGuide,
        PRODUCT_MANAGE_MANUFACTURING: MfgProductManagement,
        PRODUCT_OBSERVE_MANUFACTURING: MfgProductManagement,
        CUSTOMER_MANAGE: CustomerDevelopment,
        CUSTOMER_OBSERVE: CustomerDevelopment,
        COMPETITOR_MANAGE: CustomerDevelopment,
        COMPETITOR_OBSERVE: CustomerDevelopment,
        COMPETITORS_MANAGE: CustomerDevelopment,
        COMPETITORS_OBSERVE: CustomerDevelopment,
        CONTACT_MANAGE: CompanyContacts,
        CONTACT_OBSERVE: CompanyContacts,
        MARKETING_DASHBOARD: Dashboard,
        MY_TASK: DepartmentTasks,
        CHATROOM_MANAGE: Chatrooms,
        CHATROOM_OBSERVE: Chatrooms,
    };

    const getServiceComponent = (serviceCode) => {
        if (!serviceCode) return null;
        if (serviceComponents[serviceCode]) return serviceComponents[serviceCode];
        if (serviceCode.includes("MY_TASK")) return DepartmentTasks;
        return null;
    };

    // ✅ Auth protection
    const ProtectedRoute = ({children}) => {
        return user ? children : <Navigate to="/login" replace />;
    };

    // ✅ Privilege protection
    const PrivilegedRoute = ({allowed, children}) => {
        const hasAccess = ["ADMIN", "ALL"].includes(user?.role)
            || privilegeList.includes("ALL")
            || allowed.some(code => privilegeList.includes(String(code).toUpperCase()));
        return hasAccess ? children : <Navigate to="/" replace />;
    };

    const AdminRoute = ({children}) => {
        return user?.role === "ADMIN" ? children : <Navigate to="/" replace />;
    };

    const NonExecutiveRoute = ({children}) => {
        return user?.position !== "EXECUTIVE"
            ? children
            : <Navigate to="/my-department/tasks/my-task" replace />;
    };

    return (
        <Router>
            <div className="App">
                <Suspense fallback={<RouteLoading/>}>
                    <Routes>

                    {/* Login */}
                    <Route
                        path="/login"
                        element={
                            user
                                ? <Navigate to="/" replace/>
                                : <Login onLogin={setUser} lang={lang} setLang={setLang}/>
                        }
                    />

                    {/* Protected Layout */}
                    <Route
                        element={
                            <ProtectedRoute>
                                <Layout
                                    user={user}
                                    onLogout={() => setUser(null)}
                                    lang={lang}
                                    setLang={setLang}
                                    appVersion={version}
                                >
                                    <SafeOutlet/>
                                </Layout>
                            </ProtectedRoute>
                        }
                    >

                        {/* Home */}
                        <Route
                            path="/"
                            element={<Home user={user} lang={lang} onLogout={() => setUser(null)}/>}
                        />
                        <Route
                            path="/chatrooms"
                            element={
                                <PrivilegedRoute allowed={CHATROOM_PRIVILEGES}>
                                    <Chatrooms user={user} lang={lang}/>
                                </PrivilegedRoute>
                            }
                        />
                        <Route
                            path="/form-submissions"
                            element={
                                <PrivilegedRoute allowed={CHATROOM_PRIVILEGES}>
                                    <FormSubmissions lang={lang}/>
                                </PrivilegedRoute>
                            }
                        />
                        <Route
                            path="/profile"
                            element={<UserProfile user={user}/>}
                        />
                        <Route
                            path="/administration"
                            element={
                                <AdminRoute>
                                    <AdminPage lang={lang}/>
                                </AdminRoute>
                            }
                        />
                        <Route
                            path="/administration/user/:id"
                            element={
                                <AdminRoute>
                                    <AdminUserPage lang={lang}/>
                                </AdminRoute>
                            }
                        />
                        <Route
                            path="/my-department"
                            element={
                                <NonExecutiveRoute>
                                    <MyDepartment/>
                                </NonExecutiveRoute>
                            }
                        />
                        <Route
                            path="/my-department/create-task"
                            element={
                                <NonExecutiveRoute>
                                    <DepartmentCreateTask user={user} />
                                </NonExecutiveRoute>
                            }
                        />
                        <Route
                            path="/my-department/tasks"
                            element={
                                <Navigate to="/my-department/tasks/my-task" replace />
                            }
                        />
                        <Route
                            path="/my-department/tasks/my-task"
                            element={<DepartmentTasks user={user} mode="my" />}
                        />
                        <Route
                            path="/my-department/tasks/assigned-tasks"
                            element={<DepartmentTasks user={user} mode="assigned" />}
                        />
                        <Route
                            path="/my-department/tasks/assign-task"
                            element={<DepartmentAssignTask user={user} />}
                        />
                        <Route
                            path="/my-department/tasks/create-task"
                            element={<DepartmentCreateTask user={user} />}
                        />
                        <Route
                            path="/my-department/teams"
                            element={
                                <NonExecutiveRoute>
                                    <DepartmentTeams />
                                </NonExecutiveRoute>
                            }
                        />
                        <Route
                            path="/my-department/my-tasks"
                            element={<Navigate to="/my-department/tasks/my-task" replace />}
                        />
                        <Route
                            path="/my-department/my-tasks/:taskId"
                            element={<Navigate to="/my-department/tasks/my-task" replace />}
                        />
                        <Route
                            path="/services/assign-tasks"
                            element={<Navigate to="/my-department/tasks/assign-task" replace />}
                        />
                        <Route
                            path="/my-tasks"
                            element={<Navigate to="/my-department/tasks/my-task" replace />}
                        />
                        <Route
                            path="/my-tasks/:taskId"
                            element={<Navigate to="/my-department/tasks/my-task" replace />}
                        />
                        <Route
                            path="/tutorials-and-guide"
                            element={<TutorialsAndGuide/>}
                        />

                        {/* ✅ NEW: Single Product Route (PUT IT HERE) */}
                        <Route
                            path="/product/:id"
                            element={
                                <PrivilegedRoute allowed={["PRODUCT_MANAGE", "PRODUCT_OBSERVE"]}>
                                    <SingleProduct lang={lang} privilegeList={privilegeList}/>
                                </PrivilegedRoute>
                            }
                        />
                        {/* CDP Routes */}
                        <Route path="/customer-development" element={<PrivilegedRoute allowed={["CUSTOMER_MANAGE","CUSTOMER_OBSERVE","COMPETITOR_MANAGE","COMPETITOR_OBSERVE","COMPETITORS_MANAGE","COMPETITORS_OBSERVE"]}><CustomerDevelopment lang={lang} privilegeList={privilegeList} user={user} defaultClassification="CUSTOMER"/></PrivilegedRoute>}/>
                        <Route path="/customer-development/competitors" element={<PrivilegedRoute allowed={["CUSTOMER_MANAGE","COMPETITOR_MANAGE","COMPETITOR_OBSERVE","COMPETITORS_MANAGE","COMPETITORS_OBSERVE"]}><CustomerDevelopment lang={lang} privilegeList={privilegeList} user={user} defaultClassification="COMPETITOR"/></PrivilegedRoute>}/>
                        <Route path="/customer-development/add" element={<PrivilegedRoute allowed={["CUSTOMER_MANAGE"]}><AddEditCompany user={user} lang={lang}/></PrivilegedRoute>}/>
                        <Route path="/customer-development/edit/:id" element={<PrivilegedRoute allowed={["CUSTOMER_MANAGE"]}><AddEditCompany user={user} lang={lang}/></PrivilegedRoute>}/>
                        <Route path="/customer-development/company/:id" element={<PrivilegedRoute allowed={["CUSTOMER_MANAGE","CUSTOMER_OBSERVE"]}><CompanyProfile user={user} lang={lang}/></PrivilegedRoute>}/>
                        <Route path="/customer-development/company/:companyId/add-contact" element={<PrivilegedRoute allowed={["CUSTOMER_MANAGE"]}><AddEditContacts user={user} lang={lang}/></PrivilegedRoute>}/>
                        <Route path="/customer-development/edit-contact/:id" element={<PrivilegedRoute allowed={["CUSTOMER_MANAGE"]}><AddEditContacts user={user} lang={lang}/></PrivilegedRoute>}/>
                        <Route path="/customer-development/contact/:id" element={<PrivilegedRoute allowed={["CUSTOMER_MANAGE","CUSTOMER_OBSERVE"]}><ContactProfile user={user} lang={lang}/></PrivilegedRoute>}/>
                        <Route path="/customer-contacts" element={<PrivilegedRoute allowed={["CONTACT_MANAGE","CONTACT_OBSERVE"]}><CompanyContacts user={user} lang={lang}/></PrivilegedRoute>}/>

                        {/* Dynamic Service Routes */}
                        {user?.services
                            ?.filter(service => privilegeList.includes(String(service.serviceCode).toUpperCase()))
                            .map(service => {

                                const Component = getServiceComponent(service.serviceCode);

                                if (!Component) return null;

                                return (
                                    <React.Fragment key={service.serviceCode}>
                                        <Route
                                            path={service.route}
                                            element={<Component lang={lang} privilegeList={privilegeList} user={user} defaultClassification={service.serviceCode?.includes("COMPETITOR") ? "COMPETITOR" : "CUSTOMER"}/>}
                                        />
                                    </React.Fragment>
                                );

                            })}

                    </Route>

                    {/* 404 */}
                    <Route path="*" element={<NotFound/>}/>

                    </Routes>
                </Suspense>
            </div>
        </Router>
    );
}

export default App;
